import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  UserEvent,
  UserEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/core/v1/user_events_pb';
import { Col } from '@rematter/ui-web';
import { EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { UserAddedToOrganizationDetail } from './UserAddedToOrganizationDetail';
import { UserAdminGrantedDetail } from './UserAdminGrantedDetail';
import { UserAdminRevokedDetail } from './UserAdminRevokedDetail';
import { UserCreatedDetail } from './UserCreatedDetail';
import { UserInvitedDetail } from './UserInvitedDetail';
import { UserRemovedFromOrganizationDetail } from './UserRemovedFromOrganizationDetail';
import { UserUpdatedDetail } from './UserUpdatedDetail';

type UserEventDetailProps = {
  payload: string;
};

export const UserEventDetail = ({ payload }: UserEventDetailProps) => {
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): UserEvent | null => {
    try {
      return fromJson(UserEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title="Raw Payload (Parse Error)" />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <UserCreatedDetail event={inner.value} />;
      case 'updated':
        return <UserUpdatedDetail event={inner.value} />;
      case 'addedToOrganization':
        return <UserAddedToOrganizationDetail event={inner.value} />;
      case 'removedFromOrganization':
        return <UserRemovedFromOrganizationDetail event={inner.value} />;
      case 'invited':
        return <UserInvitedDetail event={inner.value} />;
      case 'adminGranted':
        return <UserAdminGrantedDetail event={inner.value} />;
      case 'adminRevoked':
        return <UserAdminRevokedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title="Unknown Event Type" />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return 'User Created';
      case 'updated':
        return 'User Updated';
      case 'addedToOrganization':
        return 'User Added to Organization';
      case 'removedFromOrganization':
        return 'User Removed from Organization';
      case 'invited':
        return 'User Invited';
      case 'adminGranted':
        return 'Admin Granted';
      case 'adminRevoked':
        return 'Admin Revoked';
      case undefined:
        return 'Unknown Event';
    }
  };

  return (
    <Col className="@UserEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
